import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingDown, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUCKET_LABEL = {
  too_expensive: 'Too expensive',
  not_ready_emotionally: 'Not ready emotionally',
  prefer_therapist: 'Prefer a therapist',
  missing_feature: 'Missing feature',
  bug_or_ux: 'Bug or UX friction',
  healed_dont_need: 'Healed — don\u2019t need it',
  other: 'Other',
};

export const ChurnView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/ai/churn-insights`);
      setData(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load churn insights');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) {
    return (
      <div className="text-stone-500 animate-pulse" data-testid="admin-churn-loading">
        Loading churn insights\u2026
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900" data-testid="admin-churn-error">
        {error}
      </div>
    );
  }

  const rows = data?.by_bucket || [];
  const total = data?.totals?.cancellations || 0;
  const wouldReturn = data?.totals?.would_return || 0;

  return (
    <div data-testid="admin-churn-view" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2
            className="text-2xl font-medium flex items-center gap-3"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            <TrendingDown className="w-6 h-6 text-stone-500" strokeWidth={1.5} />
            Why members are canceling
          </h2>
          <p className="text-sm text-stone-500 mt-1">
            AI-classified from the free-text reason at cancel time.
          </p>
        </div>
        <button
          onClick={load}
          className="rounded-full border border-stone-300 px-4 py-2 text-sm text-stone-700 hover:bg-stone-100 flex items-center gap-2"
          data-testid="admin-churn-refresh"
        >
          <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="rounded-2xl bg-white border border-stone-200 p-5">
          <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Total cancellations</p>
          <p className="text-3xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="admin-churn-total">
            {total}
          </p>
        </div>
        <div className="rounded-2xl bg-white border border-stone-200 p-5">
          <p className="text-xs uppercase tracking-widest text-stone-500 mb-2">Said they'd return</p>
          <p className="text-3xl font-medium" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="admin-churn-return">
            {wouldReturn}
            <span className="text-base text-stone-400 ml-2">
              {total ? `(${Math.round((wouldReturn / total) * 100)}%)` : ''}
            </span>
          </p>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-stone-200 overflow-hidden">
        <div className="grid grid-cols-[1fr_80px_80px_120px] px-5 py-3 border-b border-stone-200 bg-stone-50 text-xs uppercase tracking-widest text-stone-500">
          <div>Bucket</div>
          <div className="text-right">Count</div>
          <div className="text-right">Return</div>
          <div className="text-right">Share</div>
        </div>
        {rows.length === 0 && (
          <div className="px-5 py-8 text-center text-stone-500" data-testid="admin-churn-empty">
            No cancellations recorded yet.
          </div>
        )}
        {rows.map((row) => (
          <div
            key={row.bucket}
            className="grid grid-cols-[1fr_80px_80px_120px] px-5 py-4 border-b border-stone-100 last:border-b-0 items-center"
            data-testid={`admin-churn-row-${row.bucket}`}
          >
            <div className="text-stone-800">{BUCKET_LABEL[row.bucket] || row.bucket}</div>
            <div className="text-right text-stone-700 tabular-nums">{row.count}</div>
            <div className="text-right text-stone-500 tabular-nums">{row.would_return}</div>
            <div className="text-right">
              <div className="inline-flex items-center gap-2">
                <div className="w-16 h-2 bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-stone-700 rounded-full"
                    style={{ width: `${Math.min(100, Math.round(row.share * 100))}%` }}
                  />
                </div>
                <span className="text-sm text-stone-600 tabular-nums w-10 text-right">
                  {Math.round(row.share * 100)}%
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {data?.note && (
        <p className="text-xs text-stone-400 italic">{data.note}</p>
      )}
    </div>
  );
};

export default ChurnView;
